/**
 * OnboardingTour — Tour guiado interativo com spotlight nos elementos reais da UI.
 * Destaca itens do menu, navega entre seções e mostra tooltips contextuais.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Building2,
  Users,
  LayoutGrid,
  Rocket,
  ArrowRight,
  ArrowLeft,
  X,
  Sparkles,
  CheckCircle2,
  MousePointerClick,
} from 'lucide-react';

interface OnboardingTourProps {
  empresaNome?: string;
  usuarioNome?: string;
  onClose: () => void;
  onNavigate?: (section: string) => void;
  onExpandMenu?: (menuId: string) => void;
}

interface TourStep {
  id: string;
  type: 'modal' | 'spotlight';
  target?: string; // data-tour selector
  title: string;
  description: string;
  tip?: string;
  icon: React.ElementType;
  iconBg: string;
  iconColor: string;
  position?: 'right' | 'bottom' | 'center';
  action?: string; // section to navigate / menu to expand
  expandMenu?: string; // menu group to expand before highlighting
  ctaLabel?: string;
  ctaNavigate?: string; // navigate on CTA click
}

const TOUR_STEPS: TourStep[] = [
  {
    id: 'welcome',
    type: 'modal',
    title: 'Bem-vindo ao Escalone!',
    description: 'Vamos te guiar nos primeiros passos para configurar o sistema. É rápido — em 3 etapas tudo estará pronto.',
    icon: Rocket,
    iconBg: 'bg-indigo-100',
    iconColor: 'text-indigo-600',
    tip: 'O tour vai destacar os itens do menu para você saber exatamente onde encontrar cada coisa.',
  },
  {
    id: 'menu-organizacoes',
    type: 'spotlight',
    target: '[data-tour="menu-organizacoes"]',
    title: 'Menu: Organizações',
    description: 'Tudo começa aqui! Neste menu você encontra Empresas, Departamentos, Colaboradores e Organograma.',
    icon: Building2,
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    position: 'right',
    expandMenu: 'organizacoes',
    tip: 'Clique em "Organizações" para expandir o submenu.',
  },
  {
    id: 'menu-departamentos',
    type: 'spotlight',
    target: '[data-tour="menu-departamentos"]',
    title: '1º Passo: Departamentos',
    description: 'Crie os departamentos da sua empresa (ex: Comercial, TI, RH). Eles organizam seus colaboradores e são usados nas etapas dos fluxos.',
    icon: Building2,
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    position: 'right',
    expandMenu: 'organizacoes',
    tip: 'Clique aqui para abrir a tela de Departamentos e criar o primeiro.',
    ctaLabel: 'Ir para Departamentos',
    ctaNavigate: 'departamentos',
  },
  {
    id: 'menu-colaboradores',
    type: 'spotlight',
    target: '[data-tour="menu-colaboradores"]',
    title: '2º Passo: Colaboradores',
    description: 'Após criar os departamentos, cadastre as pessoas da equipe. Cada colaborador fica vinculado a um departamento e pode ser responsável por cards.',
    icon: Users,
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    position: 'right',
    expandMenu: 'organizacoes',
    tip: 'Cada colaborador recebe acesso ao sistema com o email cadastrado.',
    ctaLabel: 'Ir para Colaboradores',
    ctaNavigate: 'colaboradores',
  },
  {
    id: 'menu-flows',
    type: 'spotlight',
    target: '[data-tour="menu-flows"]',
    title: '3º Passo: Criar Fluxos',
    description: 'Com departamentos e colaboradores prontos, crie seu primeiro fluxo de trabalho! Um fluxo tem etapas (colunas) e cards que percorrem essas etapas.',
    icon: LayoutGrid,
    iconBg: 'bg-purple-100',
    iconColor: 'text-purple-600',
    position: 'right',
    tip: 'Dica: use os Templates prontos para começar rapidamente!',
    ctaLabel: 'Ir para Fluxos',
    ctaNavigate: 'flows',
  },
  {
    id: 'done',
    type: 'modal',
    title: 'Tudo pronto! 🎉',
    description: 'Agora você sabe onde encontrar tudo. Comece criando seus departamentos e depois siga o fluxo. Se precisar, o tour está sempre disponível.',
    icon: CheckCircle2,
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
  },
];

const OnboardingTour: React.FC<OnboardingTourProps> = ({
  empresaNome,
  usuarioNome,
  onClose,
  onNavigate,
  onExpandMenu,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const tooltipRef = useRef<HTMLDivElement>(null);
  const step = TOUR_STEPS[currentStep];
  const isFirst = currentStep === 0;
  const isLast = currentStep === TOUR_STEPS.length - 1;
  const Icon = step.icon;

  // Find and highlight target element
  const updateTarget = useCallback(() => {
    if (step.type === 'spotlight' && step.target) {
      // Ensure menu is expanded
      if (step.expandMenu) {
        onExpandMenu?.(step.expandMenu);
      }

      // Small delay to let DOM update after menu expansion
      const timer = setTimeout(() => {
        const el = document.querySelector(step.target!);
        if (el) {
          const rect = el.getBoundingClientRect();
          setTargetRect(rect);
        } else {
          setTargetRect(null);
        }
      }, 150);
      return () => clearTimeout(timer);
    } else {
      setTargetRect(null);
    }
  }, [step, onExpandMenu]);

  useEffect(() => {
    updateTarget();
    const handleResize = () => updateTarget();
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize, true);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize, true);
    };
  }, [updateTarget]);

  // Position tooltip next to target
  useEffect(() => {
    if (!targetRect || step.type === 'modal') {
      setTooltipStyle({});
      return;
    }

    const padding = 16;
    const tooltipWidth = 360;
    const pos = step.position || 'right';

    if (pos === 'right') {
      let top = targetRect.top + targetRect.height / 2;
      let left = targetRect.right + padding;

      // If tooltip goes off-screen right, position below instead
      if (left + tooltipWidth > window.innerWidth - padding) {
        left = targetRect.left;
        top = targetRect.bottom + padding;
      }

      // Keep tooltip in viewport vertically
      const maxTop = window.innerHeight - 320;
      top = Math.max(padding, Math.min(top, maxTop));

      setTooltipStyle({
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
        width: `${tooltipWidth}px`,
        transform: 'translateY(-30%)',
      });
    } else if (pos === 'bottom') {
      setTooltipStyle({
        position: 'fixed',
        top: `${targetRect.bottom + padding}px`,
        left: `${targetRect.left}px`,
        width: `${tooltipWidth}px`,
      });
    }
  }, [targetRect, step]);

  const handleNext = () => {
    if (isLast) {
      onClose();
    } else {
      setCurrentStep(s => s + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirst) {
      setCurrentStep(s => s - 1);
    }
  };

  const handleCTA = () => {
    if (step.ctaNavigate) {
      onNavigate?.(step.ctaNavigate);
    }
  };

  // ── MODAL type step ──
  if (step.type === 'modal') {
    return (
      <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden relative">
          {/* Close */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors z-10"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Progress dots */}
          <div className="flex items-center justify-center gap-1.5 pt-6">
            {TOUR_STEPS.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentStep(i)}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === currentStep
                    ? 'w-6 bg-indigo-600'
                    : i < currentStep
                    ? 'w-1.5 bg-indigo-300'
                    : 'w-1.5 bg-gray-200'
                }`}
              />
            ))}
          </div>

          {/* Content */}
          <div className="px-8 pt-6 pb-4 text-center">
            <div className={`w-16 h-16 mx-auto rounded-2xl ${step.iconBg} flex items-center justify-center mb-5`}>
              <Icon className={`w-8 h-8 ${step.iconColor}`} />
            </div>

            {isFirst && usuarioNome && (
              <p className="text-sm text-indigo-600 font-semibold mb-1">
                Olá, {usuarioNome.split(' ')[0]}! 👋
              </p>
            )}

            <h2 className="text-xl font-bold text-gray-900 mb-1">{step.title}</h2>

            {isFirst && empresaNome && (
              <div className="inline-flex items-center gap-2 bg-indigo-50 text-indigo-700 text-xs font-medium px-3 py-1.5 rounded-full mb-4">
                <Sparkles className="w-3 h-3" />
                {empresaNome}
              </div>
            )}

            <p className="text-sm text-gray-600 leading-relaxed">{step.description}</p>

            {step.tip && (
              <div className="mt-4 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3">
                <p className="text-xs text-amber-700">
                  💡 {step.tip}
                </p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="px-8 pb-6 pt-2 flex gap-3">
            {!isFirst && (
              <button
                onClick={handlePrev}
                className="px-4 py-3 rounded-xl text-sm font-semibold text-gray-600 bg-gray-50 hover:bg-gray-100 transition-all border border-gray-200 flex items-center justify-center gap-1"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={handleNext}
              className="flex-1 py-3 px-4 rounded-xl text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 transition-all shadow-sm flex items-center justify-center gap-2"
            >
              {isLast ? (
                <>
                  <CheckCircle2 className="w-4 h-4" /> Começar a usar
                </>
              ) : isFirst ? (
                <>
                  Vamos lá! <ArrowRight className="w-4 h-4" />
                </>
              ) : (
                <>
                  Próximo <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>

          {/* Skip */}
          {!isLast && (
            <div className="text-center pb-5">
              <button
                onClick={onClose}
                className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
              >
                Pular tour
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── SPOTLIGHT type step ──
  return (
    <div className="fixed inset-0 z-[10000]">
      {/* Overlay with spotlight cutout */}
      <svg
        className="absolute inset-0 w-full h-full"
        style={{ pointerEvents: 'none' }}
      >
        <defs>
          <mask id="spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {targetRect && (
              <rect
                x={targetRect.left - 6}
                y={targetRect.top - 4}
                width={targetRect.width + 12}
                height={targetRect.height + 8}
                rx="10"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0,0,0,0.55)"
          mask="url(#spotlight-mask)"
        />
      </svg>

      {/* Pulse ring around target */}
      {targetRect && (
        <div
          className="absolute border-2 border-indigo-400 rounded-xl pointer-events-none"
          style={{
            left: targetRect.left - 6,
            top: targetRect.top - 4,
            width: targetRect.width + 12,
            height: targetRect.height + 8,
            boxShadow: '0 0 0 4px rgba(99,102,241,0.15), 0 0 20px rgba(99,102,241,0.2)',
            animation: 'pulse 2s ease-in-out infinite',
          }}
        />
      )}

      {/* Clickable overlay to go next */}
      <div
        className="absolute inset-0"
        onClick={handleNext}
        style={{ cursor: 'pointer' }}
      />

      {/* Tooltip card */}
      <div
        ref={tooltipRef}
        className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden pointer-events-auto"
        style={{
          ...tooltipStyle,
          zIndex: 10001,
          maxWidth: '380px',
          minWidth: '300px',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Arrow indicator pointing to target */}
        {targetRect && (step.position === 'right' || !step.position) && (
          <div
            className="absolute w-3 h-3 bg-white border-l border-t border-gray-200 transform -rotate-45"
            style={{
              left: '-7px',
              top: '30%',
            }}
          />
        )}

        {/* Progress bar */}
        <div className="h-1 bg-gray-100">
          <div
            className="h-full bg-indigo-600 transition-all duration-500 rounded-r"
            style={{ width: `${((currentStep + 1) / TOUR_STEPS.length) * 100}%` }}
          />
        </div>

        <div className="p-5">
          {/* Step counter */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-lg ${step.iconBg} flex items-center justify-center`}>
                <Icon className={`w-4 h-4 ${step.iconColor}`} />
              </div>
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                Passo {currentStep} de {TOUR_STEPS.length - 1}
              </span>
            </div>
            <button
              onClick={onClose}
              className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          <h3 className="text-base font-bold text-gray-900 mb-1">{step.title}</h3>
          <p className="text-sm text-gray-600 leading-relaxed mb-3">{step.description}</p>

          {/* Visual hint: pointing at menu */}
          <div className="flex items-center gap-2 bg-indigo-50 rounded-lg px-3 py-2 mb-3">
            <MousePointerClick className="w-4 h-4 text-indigo-500 shrink-0" />
            <p className="text-xs text-indigo-700 font-medium">
              {step.id === 'menu-organizacoes'
                ? 'Este é o menu "Organizações" na barra lateral ←'
                : step.id === 'menu-departamentos'
                ? 'Clique em "Departamentos" no menu lateral ←'
                : step.id === 'menu-colaboradores'
                ? 'Clique em "Colaboradores" no menu lateral ←'
                : step.id === 'menu-flows'
                ? 'Clique em "Fluxos" no menu lateral ←'
                : 'Elemento destacado na tela ←'}
            </p>
          </div>

          {step.tip && (
            <p className="text-xs text-gray-500 mb-3">
              💡 {step.tip}
            </p>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrev}
              className="px-3 py-2 rounded-lg text-xs font-medium text-gray-500 hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
            </button>

            {step.ctaLabel && step.ctaNavigate && (
              <button
                onClick={handleCTA}
                className="flex-1 py-2 px-3 rounded-lg text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 transition-all shadow-sm flex items-center justify-center gap-1.5"
              >
                {step.ctaLabel} <ArrowRight className="w-3 h-3" />
              </button>
            )}

            <button
              onClick={handleNext}
              className={`py-2 px-3 rounded-lg text-xs font-medium transition-colors ${
                step.ctaLabel
                  ? 'text-gray-500 hover:bg-gray-100'
                  : 'flex-1 font-bold text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm flex items-center justify-center gap-1.5'
              }`}
            >
              {isLast ? 'Finalizar' : step.ctaLabel ? 'Pular →' : (
                <>Próximo <ArrowRight className="w-3 h-3" /></>
              )}
            </button>
          </div>

          {/* Skip all */}
          <div className="text-center mt-3">
            <button
              onClick={onClose}
              className="text-[10px] text-gray-400 hover:text-gray-600 transition-colors"
            >
              Pular tour completo
            </button>
          </div>
        </div>
      </div>

      {/* Pulse animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 0 4px rgba(99,102,241,0.15), 0 0 20px rgba(99,102,241,0.1); }
          50% { box-shadow: 0 0 0 8px rgba(99,102,241,0.1), 0 0 30px rgba(99,102,241,0.2); }
        }
      `}</style>
    </div>
  );
};

export default OnboardingTour;
